import React from 'react';
import {
  Form,
  Input,
  Card,
  Typography,
  Radio,
  Row,
  Col,
  Tag,
  Button,
  Space,
  message,
} from 'antd';
import { AutoComplete } from 'antd';
import { adminSentenceApi } from '@/services/api';

const { Title } = Typography;
const { TextArea } = Input;

interface FillBlankWritingSectionProps {
  title?: string; // 유형별 타이틀
  onFullTextChange?: (value: string) => void; // 하위 호환용 (미사용)
  onTranslationChange?: (value: string) => void; // 하위 호환용 (미사용)
  onCorrectWordChange?: (value: string) => void; // 하위 호환용 (미사용)
  recordingEnabled?: boolean; // 하위 호환용 (미사용)
  onRecordingEnabledChange?: (enabled: boolean) => void; // 하위 호환용 (미사용)
}

type CorrectWordsMap = Record<number, string[]>;

const FillBlankWritingSection: React.FC<FillBlankWritingSectionProps> = ({
  title = '빈칸 채우기',
}) => {
  const form = Form.useFormInstance();
  const [options, setOptions] = React.useState<{ key: string; value: string; label: string; item: any }[]>([]);
  const [correctWordsMap, setCorrectWordsMap] = React.useState<CorrectWordsMap>({});

  const buildOptions = (list: any[]) => {
    const toKo = {
      gender: (v: string) => v === 'male' ? '남성' : v === 'female' ? '여성' : v,
      age: (v: string) => (
        v === 'child' ? '아동' : v === 'young' ? '청년' : v === 'adult' ? '성인' : v === 'elder' ? '노년' : v
      ),
      speed: (v: string) => v === 'slow' ? '느림' : v === 'normal' ? '보통' : v === 'fast' ? '빠름' : v,
      pitch: (v: string) => v === 'low' ? '낮음' : v === 'medium' ? '보통' : v === 'high' ? '높음' : v,
    } as const;
    return list.map((s: any) => {
      const g = toKo.gender(String(s.gender || ''));
      const a = toKo.age(String(s.age || ''));
      const sp = toKo.speed(String(s.speed || ''));
      const p = toKo.pitch(String(s.pitch || ''));
      const text = String(s.text || '');
      return {
        key: `${s.id}-${s.gender}-${s.age}-${s.speed}-${s.pitch}`,
        value: text,
        label: `${text} (${g}|${a}|${sp}|${p})`,
        item: s,
      };
    });
  };

  const searchSentences = (query: string) => {
    window.clearTimeout((searchSentences as any)._t);
    (searchSentences as any)._t = window.setTimeout(async () => {
      try {
        const res = await adminSentenceApi.list({ q: query, page: 0, size: 100 });
        const data = res?.data || res;
        const content = data?.content || data?.data?.content || [];
        setOptions(buildOptions(content));
      } catch (_) {
        setOptions([]);
      }
    }, 250);
  };

  const syncMapFromForm = () => {
    const current = form.getFieldValue('fb_items');
    if (!Array.isArray(current)) return;
    const map: CorrectWordsMap = {};
    current.forEach((item: any, idx: number) => {
      const list = Array.isArray(item?.correctWords) ? item.correctWords : [];
      map[idx] = list;
    });
    setCorrectWordsMap(map);
  };

  // 초기 로드 시 Form.List 기본값 보정 및 correctWords 맵 동기화
  React.useEffect(() => {
    const current = form.getFieldValue('fb_items');
    if (Array.isArray(current) && current.length > 0) {
      syncMapFromForm();
      return;
    }
    const defaultItem = { fullText: '', translation: '', correctWords: [], recordingEnabled: false };
    form.setFieldValue('fb_items', [defaultItem]);
    setCorrectWordsMap({ 0: [] });
  }, [form]);

  const ensureCorrectWords = (fieldIndex: number, next: string[]) => {
    setCorrectWordsMap((prev) => ({ ...prev, [fieldIndex]: next }));
    form.setFieldValue(['fb_items', fieldIndex, 'correctWords'], next);
  };

  const addWord = (fieldIndex: number, value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    const prev = correctWordsMap[fieldIndex] || [];
    if (prev.includes(trimmed)) return;
    const next = [...prev, trimmed];
    ensureCorrectWords(fieldIndex, next);
  };

  const removeWord = (fieldIndex: number, idx: number) => {
    const prev = correctWordsMap[fieldIndex] || [];
    const next = prev.filter((_, i) => i !== idx);
    ensureCorrectWords(fieldIndex, next);
  };

  const validateCorrectWords = (_rule: any, _value: any, index: number) => {
    const list = form.getFieldValue(['fb_items', index, 'correctWords']);
    const has = Array.isArray(list) && list.some((w: any) => !!String(w).trim());
    return has
      ? Promise.resolve()
      : Promise.reject(new Error('정답 단어를 한 개 이상 추가해주세요.'));
  };

  const handleRemove = (remove: (index: number | number[]) => void, fieldsLen: number, index: number) => {
    if (fieldsLen <= 1) {
      message.warning('문항은 최소 1개 이상이어야 합니다.');
      return;
    }
    remove(index);
    setTimeout(() => syncMapFromForm(), 0);
  };

  return (
    <Card className='pb-5 border-b border-gray-200'>
      <Title level={4} className='mb-4'>{title}</Title>

      <Form.List name="fb_items">
        {(fields, { add, remove }) => (
          <>
            <div className="space-y-6">
              {fields.map((field, idx) => {
                const words = (form.getFieldValue(['fb_items', field.name, 'correctWords']) || correctWordsMap[field.name] || []) as string[];
                return (
                  <Card key={field.key} size="small" title={`문항 ${idx + 1}`}>
                    <Row gutter={16}>
                      <Col span={24}>
                        <Form.Item
                          label="녹음 여부"
                          name={[field.name, 'recordingEnabled']}
                          rules={[{ required: true, message: '녹음 여부를 선택해주세요' }]}
                        >
                          <Radio.Group>
                            <Radio value={true}>녹음</Radio>
                            <Radio value={false}>미녹음</Radio>
                          </Radio.Group>
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={16}>
                      <Col span={24}>
                        <Form.Item
                          label="전체 지문"
                          name={[field.name, 'fullText']}
                          rules={[{ required: true, message: '전체 지문을 선택해주세요' }]}
                        >
                          <AutoComplete
                            placeholder="문장을 검색/선택하세요"
                            size="large"
                            onFocus={() => searchSentences('')}
                            onSearch={searchSentences}
                            options={options}
                            filterOption={false}
                            onSelect={(val: string, option: any) => {
                              const s = option?.item;
                              form.setFieldsValue({
                                fb_items: (form.getFieldValue('fb_items') || []).map((item: any, i: number) => {
                                  if (i !== field.name) return item;
                                  return {
                                    ...item,
                                    fullText: s?.text ?? val,
                                    translation: s?.translation ?? item?.translation ?? '',
                                    sentenceId: s?.id || undefined,
                                    audioUrl: s?.audioUrl || item?.audioUrl,
                                  };
                                })
                              });
                            }}
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item
                          label="해석"
                          name={[field.name, 'translation']}
                          rules={[{ required: true, message: '해석을 입력해주세요' }]}
                        >
                          <Input placeholder="해석을 입력하세요" size="large" />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="정답 단어" extra="엔터를 누르면 태그로 추가됩니다">
                          <Input
                            placeholder="정답 단어를 입력 후 엔터"
                            size="large"
                            onPressEnter={(e) => {
                              e.preventDefault();
                              const input = (e.target as HTMLInputElement).value || '';
                              addWord(field.name, input);
                              (e.target as HTMLInputElement).value = '';
                            }}
                          />
                        </Form.Item>
                        <Form.Item
                          name={[field.name, 'correctWords']}
                          rules={[{ validator: (_r, v) => validateCorrectWords(_r, v, field.name) }]}
                          style={{ marginTop: -12 }}
                        >
                          <div>
                            {words.map((w, wordIdx) => (
                              <Tag
                                key={`${w}-${wordIdx}`}
                                closable
                                onClose={(ev) => {
                                  ev.preventDefault();
                                  removeWord(field.name, wordIdx);
                                }}
                                className='mb-2'
                              >
                                {w}
                              </Tag>
                            ))}
                          </div>
                        </Form.Item>
                      </Col>
                    </Row>

                    <Space style={{ marginTop: 4 }}>
                      <Button danger type="text" onClick={() => handleRemove(remove, fields.length, idx)}>
                        문항 삭제
                      </Button>
                    </Space>
                  </Card>
                );
              })}
            </div>

            <div className="mt-4">
              <Button
                type="dashed"
                block
                onClick={() => {
                  add({ fullText: '', translation: '', correctWords: [], recordingEnabled: false });
                  const nextIndex = fields.length;
                  setCorrectWordsMap((prev) => ({ ...prev, [nextIndex]: [] }));
                  setTimeout(() => {
                    const list = form.getFieldValue('fb_items') || [];
                    if (Array.isArray(list)) {
                      const nextList = [...list];
                      if (!nextList[nextIndex]) nextList[nextIndex] = {};
                      if (!Array.isArray(nextList[nextIndex].correctWords)) {
                        nextList[nextIndex].correctWords = [];
                      }
                      form.setFieldValue('fb_items', nextList);
                      syncMapFromForm();
                    }
                  }, 0);
                }}
              >
                문항 추가
              </Button>
            </div>
          </>
        )}
      </Form.List>
    </Card>
  );
};

export default FillBlankWritingSection; 